import { Injectable } from '@angular/core';
import * as dayjs from 'dayjs';
import { CartModel } from 'src/app/models/cart.model';
import { RequestHeaderModel } from '../models/request-header.model';
import { RequestLineModel } from '../models/request-line.model';
import { MenuItem, MenuSection } from '../models/rich-data.model';
import { CaptionPipe } from '../pipes/caption.pipe';
import { AuthService } from './auth.service';
import { DataService } from './data.service';
import { GetTranslationService } from './get-translation.service';
import { RequestService } from './request.service';
import { ToastMachineService } from './toast-machine.service';
import {
  ExternalRequestLineModel,
  ExternalRequestModel,
} from '../models/external-request.model';
import { TaskType } from '../models/task-type.model';
import { HotelBuilding } from '../models/building.model';
import { TaskTypeService } from './task-type.service';
import { BuildingService } from './building.service';
import { ReservationStateService } from '../pages/restaurant-booking/state/reservation-state.service';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  cart: CartModel[] = [];
  catalogCounts = {};
  catalogPrices = {};

  currencyCode: string = null;

  totalAmount = 0;
  totalCount = 0;
  TotalWithCharge = 0;
  selectedReceipt = 0;
  cover = 1;

  orderType = 'order';
  deliveryTime = dayjs(new Date()).add(45, 'minute').toISOString();
  tableNo = '0';

  succesMessage = {
    tr: 'Talebiniz başarıyla alındı.',
    en: 'Your request has been received successfully.',
    ru: 'Ваш запрос был успешно получен.',
    ar: 'تم استلام طلبك بنجاح.',
    fa: 'درخواست شما با موفقیت دریافت شد',
    de: 'Ihre Anfrage wurde erfolgreich empfangen.',
    fr: 'Votre demande a été reçue avec succès.',
  };

  constructor(
    private toaster: ToastMachineService,
    private dataServ: DataService,
    private translate: CaptionPipe,
    private translator: GetTranslationService,
    private authServ: AuthService,
    private requestServ: RequestService,
    private typeServ: TaskTypeService,
    private buildingServ: BuildingService,
    private reservationState: ReservationStateService
  ) {}

  clearCart() {
    this.cart = [];
    this.totalAmount = 0;
    this.totalCount = 0;
    this.currencyCode = null;
    this.selectedReceipt = 0;

    this.calcTotal();
  }

  calcTotal() {
    this.totalCount = 0;
    this.totalAmount = 0;
    this.TotalWithCharge = 0;

    this.cart.map((x) => (x.size = JSON.parse(JSON.stringify(x.size))));

    this.cart.forEach((item) => {
      let unitPrice = 0;

      // Price display logic
      if (this.dataServ.menuItem.show_price) {
        // Offer active for members?
        if (
          item.size.activate_offer &&
          this.dataServ.rData.discount_for_only_member &&
          this.authServ.isLogged
        ) {
          unitPrice = item.size.offer_price;
        } else if (
          item.size.activate_offer &&
          this.dataServ.rData.discount_for_only_member &&
          !this.authServ.isLogged
        ) {
          unitPrice = item.size.price;
        }
        // General discount?
        else if (
          !item.size.activate_offer &&
          this.dataServ.rData.general_discount_percentage
        ) {
          unitPrice =
            item.size.price -
            (item.size.price *
              this.dataServ.rData.general_discount_percentage) /
              100;
        }
        // No discount
        else {
          unitPrice = item.size.price;
        }
      }

      // Calculate item quantity and price
      this.totalCount += item.size.quantity;
      this.totalAmount += unitPrice * item.size.quantity;
      this.currencyCode = item.size.currency_code;

      item.amount = unitPrice * item.size.quantity;
      item.basePrice = unitPrice;

      // Calculate catalog groups
      if (this.catalogCounts[item.product.menu_item_catalog_group_id]) {
        this.catalogCounts[item.product.menu_item_catalog_group_id] +=
          item.size.quantity;
        this.catalogPrices[item.product.menu_item_catalog_group_id] +=
          item.amount;
      } else {
        this.catalogCounts[item.product.menu_item_catalog_group_id] =
          item.size.quantity;
        this.catalogPrices[item.product.menu_item_catalog_group_id] =
          item.amount;
      }

      // Remove items with quantity 0
      if (item.size.quantity === 0) {
        this.cart = this.cart.filter((x) => x.size.id !== item.size.id);
      }
    });

    // Apply service charge ONCE after calculating all items
    if (this.dataServ.menuItem.fix_menu) {
      const extraCharge = this.dataServ.menuItem.service_charge || 0;

      // Only apply if totalAmount is below free service threshold
      if (this.totalAmount < this.dataServ.menuItem.free_service_over_amount) {
        // Service charge type: 1 = percentage, 0 = fixed amount
        if (this.dataServ.menuItem.service_charge_type === 1) {
          const percentageCharge = (this.totalAmount * extraCharge) / 100;
          this.totalAmount += percentageCharge;
          this.TotalWithCharge = percentageCharge;
        } else if (this.dataServ.menuItem.service_charge_type === 0) {
          this.totalAmount += extraCharge;
          this.TotalWithCharge = extraCharge;
        }
      }
    }
  }

  request(
    orderDate: Date | string,
    notes: string,
    menuSection: MenuSection,
    menuItem: MenuItem,
    table?: string,
    status?: string
  ): Promise<RequestHeaderModel> {
    return new Promise(async (resolve, reject) => {
      if (!this.authServ.profile) {
        await this.authServ.getMe();
      }

      let taskType: TaskType = null;
      let building: HotelBuilding = null;

      if (this.dataServ.menuItem.task_type_id) {
        taskType = await this.typeServ.getType(
          this.dataServ.menuItem.task_type_id
        );
        building = (await this.buildingServ.getBuildings()).find(
          (b) => b.name === this.authServ.profile.room_no
        );
      }

      const REQ = new RequestHeaderModel(
        this.authServ.profile,
        notes,
        this.translate.transform(
          menuSection.title,
          this.dataServ.rData.hotel_language || 'tr'
        ),
        this.translate.transform(
          menuSection.title,
          this.dataServ.rData.hotel_language || 'tr'
        ),
        menuItem,
        this.translate.transform(
          menuSection.title,
          this.dataServ.rData.hotel_language || 'tr'
        ),
        this.translate.transform(
          menuSection.menu_categories.find(
            (x) => x.id === menuItem.menu_category_id
          ).title,
          this.dataServ.rData.hotel_language || 'tr'
        ),
        this.translate.transform(
          menuItem.name,
          this.dataServ.rData.hotel_language || 'tr'
        ),
        orderDate,
        this.dataServ.menuItem.form_fields,
        null,
        taskType,
        building
      );

      if (status === 'paymentProcessing') {
        REQ.status = '';
        REQ.payment_type = 'Online';
      }

      if (table) {
        REQ.room_no = `Table : ${table} - Room: ${this.authServ.profile.room_no}`;
        REQ.capacity_name = table;
      }

      if (this.totalAmount && this.currencyCode) {
        REQ.total_amount = this.totalAmount;
        REQ.currency_code = this.currencyCode;
      }

      REQ.line_type = 'Menu Item Catalog';
      this.cart = [...new Set(this.cart)];

      this.cart.forEach((item, index) => {
        if (item && item.size.quantity > 0) {""
          const rLine: RequestLineModel = new RequestLineModel(
            index + 1,
            REQ,
            'Menu Item Catalog',
            this.translate.transform(
              item.product.name,
              this.dataServ.rData.hotel_language || 'tr'
            ),
            this.translate.transform(
              item.product.name,
              this.dataServ.rData.hotel_language || 'tr'
            ),
            this.translate.transform(
              item.product.description,
              this.dataServ.rData.hotel_language || 'tr'
            ),
            item.size.quantity,
            item.basePrice,
            item.size.currency_code,
            this.translate.transform(
              item.size.size,
              this.dataServ.rData.hotel_language || 'tr'
            ),
            item.amount
          );

          rLine.note = item.note;

          REQ.requestlines.push(rLine);
        }
      });

      try {
        const RESP = await this.requestServ.postrequest([REQ]);

        if (!(RESP instanceof Array)) {
          this.toaster.toastMachine(RESP.message, 'danger', 4000);
          reject();
        }

        this.cart = [];
        this.calcTotal();
        this.clearCart();

        const MSG = await this.translator.getTranslate(
          'We have successfully received your request.'
        );
        this.toaster.toastMachine(MSG, 'primary', 4000);
        resolve(RESP[0]);
      } catch {
        reject();
      }
    });
  }

  externalRequest(
    request: ExternalRequestModel
  ): Promise<ExternalRequestModel> {
    return new Promise(async (resolve, reject) => {
      this.cart = [...new Set(this.cart)];
      request.request_lines = [];

      this.cart.forEach((item, index) => {
        if (item && item.size.quantity > 0) {
          const line: ExternalRequestLineModel = new ExternalRequestLineModel();
          line.type = 'Menu Item Catalog';
          line.no = item.product.id;
          line.line_no = index;
          line.name = this.translate.transform(
            item.product.name,
            this.dataServ.rData.hotel_language || 'tr'
          );
          line.description = this.translate.transform(
            item.product.description,
            this.dataServ.rData.hotel_language || 'tr'
          );
          line.quantity = item.size.quantity;
          line.unit_price = item.basePrice;
          line.currency_code = item.size.currency_code;
          line.unit_of_measure = this.translate.transform(
            item.size.size,
            this.dataServ.rData.hotel_language || 'tr'
          );
          line.amount = line.quantity * line.unit_price;
          line.note = item.note;
          request.amount += line.amount;

          request.request_lines.push(line);
        }
      });

      try {
        const RESP = await this.requestServ.exapiPostrequest(request);

        this.cart = [];
        this.calcTotal();
        this.clearCart();

        const MSG = await this.translator.getTranslate(
          'We have successfully received your request.'
        );
        this.toaster.toastMachine(MSG, 'primary', 3000);
        resolve(RESP[0]);
      } catch {
        reject();
      }
    });
  }
}
